import React from 'react';
import { Box, Typography, Chip, Divider, Avatar } from '@mui/material';
import { Email, Phone, LinkedIn, LocationOn, Language } from '@mui/icons-material';
import MarkdownRenderer from '../components/MarkdownRenderer';

const ModernTemplate = ({ candidateData, customizations }) => {
  const {
    primaryColor = '#2196F3',
    secondaryColor = '#FFC107',
    fontFamily = 'Roboto',
    fontSize = 14,
    spacing = 16,
    layout = 'two-column'
  } = customizations || {};

  const formatDate = (date) => {
    if (!date) return 'Present';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate) return '';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years > 0 && remainingMonths > 0) {
      return `${years}y ${remainingMonths}m`;
    } else if (years > 0) {
      return `${years}y`;
    } else {
      return `${remainingMonths}m`;
    }
  };

  return (
    <Box
      sx={{
        fontFamily,
        fontSize: `${fontSize}px`,
        lineHeight: 1.5,
        color: '#333',
        backgroundColor: 'white',
        minHeight: '297mm',
        width: '210mm',
        padding: `${spacing}px`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4, position: 'relative' }}>
        {/* Background Accent */}
        <Box
          sx={{
            position: 'absolute',
            top: -spacing,
            left: -spacing,
            right: -spacing,
            height: '120px',
            background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
            zIndex: 0
          }}
        />
        
        <Box sx={{ position: 'relative', zIndex: 1, pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {candidateData.dp && (
              <Avatar
                src={candidateData.dp}
                sx={{
                  width: 80,
                  height: 80,
                  mr: 3,
                  border: '4px solid white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 1,
                  fontSize: `${fontSize * 2.2}px`
                }}
              >
                {candidateData.name}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 300,
                  fontSize: `${fontSize * 1.3}px`
                }}
              >
                {candidateData.currentRole} {candidateData.currentCompany && `at ${candidateData.currentCompany}`}
              </Typography>
            </Box>
          </Box>

          {/* Contact Information */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              color: 'rgba(255,255,255,0.9)',
              fontSize: `${fontSize * 0.9}px`
            }}
          >
            {candidateData.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Email sx={{ fontSize: 16 }} />
                {candidateData.email}
              </Box>
            )}
            {candidateData.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Phone sx={{ fontSize: 16 }} />
                {candidateData.phone}
              </Box>
            )}
            {candidateData.linkedin && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LinkedIn sx={{ fontSize: 16 }} />
                LinkedIn Profile
              </Box>
            )}
            {candidateData.currentLocation && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn sx={{ fontSize: 16 }} />
                {candidateData.currentLocation}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {layout === 'two-column' ? (
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Left Column */}
          <Box sx={{ flex: 1 }}>
            {/* Professional Summary */}
            {candidateData.headline && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: primaryColor,
                    fontWeight: 'bold',
                    mb: 2,
                    fontSize: `${fontSize * 1.2}px`,
                    borderBottom: `2px solid ${primaryColor}`,
                    paddingBottom: 1
                  }}
                >
                  PROFESSIONAL SUMMARY
                </Typography>
                <MarkdownRenderer 
                  content={candidateData.headline}
                  typography={{ fontSize: `${fontSize}px`, lineHeight: 1.6 }}
                />
              </Box>
            )}

            {/* Experience */}
            {candidateData.experience && candidateData.experience.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: primaryColor,
                    fontWeight: 'bold',
                    mb: 2,
                    fontSize: `${fontSize * 1.2}px`,
                    borderBottom: `2px solid ${primaryColor}`,
                    paddingBottom: 1
                  }}
                >
                  PROFESSIONAL EXPERIENCE
                </Typography>
                {candidateData.experience.map((exp, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                            color: '#333',
                            fontSize: `${fontSize * 1.1}px`
                          }}
                        >
                          {exp.designation}
                        </Typography>
                        <Typography
                          sx={{
                            color: primaryColor,
                            fontWeight: 'medium',
                            fontSize: `${fontSize}px`
                          }}
                        >
                          {exp.organization}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          sx={{
                            fontSize: `${fontSize * 0.9}px`,
                            color: '#666',
                            fontWeight: 'medium'
                          }}
                        >
                          {formatDate(exp.startDate)} - {formatDate(exp.endDate)}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: `${fontSize * 0.8}px`,
                            color: '#888'
                          }}
                        >
                          {calculateDuration(exp.startDate, exp.endDate)}
                        </Typography>
                      </Box>
                    </Box>
                    {exp.description && (
                      <MarkdownRenderer
                        content={exp.description}
                        typography={{
                          fontSize: `${fontSize * 0.95}px`,
                          lineHeight: 1.5,
                          color: '#555',
                          mb: 1
                        }}
                      />
                    )}
                    {exp.skills && exp.skills.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {exp.skills.slice(0, 6).map((skill, skillIndex) => (
                          <Chip
                            key={skillIndex}
                            label={typeof skill === 'object' && skill !== null ? (typeof skill.name === 'string' ? skill.name : (skill.name ? String(skill.name) : (skill.id ? String(skill.id) : '[Skill]'))) : String(skill)}
                            size="small"
                            sx={{
                              backgroundColor: `${primaryColor}15`,
                              color: primaryColor,
                              fontSize: `${fontSize * 0.75}px`,
                              height: 24
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Right Column */}
          <Box sx={{ width: '280px' }}>
            {/* Skills */}
            {candidateData.skills && candidateData.skills.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: primaryColor,
                    fontWeight: 'bold',
                    mb: 2,
                    fontSize: `${fontSize * 1.2}px`,
                    borderBottom: `2px solid ${primaryColor}`,
                    paddingBottom: 1
                  }}
                >
                  CORE SKILLS
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {candidateData.skills.slice(0, 15).map((skill, index) => (
                    <Chip
                      key={index}
                      label={typeof skill === 'object' && skill !== null ? (typeof skill.name === 'string' ? skill.name : (skill.name ? String(skill.name) : (skill.id ? String(skill.id) : '[Skill]'))) : String(skill)}
                      sx={{
                        backgroundColor: primaryColor,
                        color: 'white',
                        fontSize: `${fontSize * 0.85}px`,
                        '&:hover': {
                          backgroundColor: `${primaryColor}dd`
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Education */}
            {candidateData.education && candidateData.education.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: primaryColor,
                    fontWeight: 'bold',
                    mb: 2,
                    fontSize: `${fontSize * 1.2}px`,
                    borderBottom: `2px solid ${primaryColor}`,
                    paddingBottom: 1
                  }}
                >
                  EDUCATION
                </Typography>
                {candidateData.education.map((edu, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: `${fontSize}px`,
                        color: '#333'
                      }}
                    >
                      {edu.degree}
                    </Typography>
                    <Typography
                      sx={{
                        color: primaryColor,
                        fontSize: `${fontSize * 0.9}px`,
                        fontWeight: 'medium'
                      }}
                    >
                      {edu.institution}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: `${fontSize * 0.85}px`,
                        color: '#666'
                      }}
                    >
                      {formatDate(edu.startDate)} - {formatDate(edu.endDate)}
                    </Typography>
                    {edu.marks && (
                      <Typography
                        sx={{
                          fontSize: `${fontSize * 0.8}px`,
                          color: '#888'
                        }}
                      >
                        Grade: {edu.marks}
                      </Typography>
                    )}
                    {edu.description && (
                      <MarkdownRenderer
                        content={edu.description}
                        typography={{
                          fontSize: `${fontSize * 0.85}px`,
                          lineHeight: 1.5,
                          color: '#555',
                          mt: 0.5
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Experience Summary */}
            {candidateData.experience_yrs && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: primaryColor,
                    fontWeight: 'bold',
                    mb: 2,
                    fontSize: `${fontSize * 1.2}px`,
                    borderBottom: `2px solid ${primaryColor}`,
                    paddingBottom: 1
                  }}
                >
                  EXPERIENCE
                </Typography>
                <Typography
                  sx={{
                    fontSize: `${fontSize * 1.5}px`,
                    fontWeight: 'bold',
                    color: primaryColor
                  }}
                >
                  {Math.floor(candidateData.experience_yrs)} Years
                </Typography>
                <Typography
                  sx={{
                    fontSize: `${fontSize * 0.9}px`,
                    color: '#666'
                  }}
                >
                  {Math.round((candidateData.experience_yrs % 1) * 12)} Months
                </Typography>
              </Box>
            )}

            {/* Additional Information */}
            <Box>
              {candidateData.compensation && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: `${fontSize * 0.9}px`,
                      color: '#666',
                      fontWeight: 'medium'
                    }}
                  >
                    Current CTC: ₹{candidateData.compensation} LPA
                  </Typography>
                </Box>
              )}
              {candidateData.expectation && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: `${fontSize * 0.9}px`,
                      color: '#666',
                      fontWeight: 'medium'
                    }}
                  >
                    Expected CTC: ₹{candidateData.expectation} LPA
                  </Typography>
                </Box>
              )}
              {candidateData.noticePeriod && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: `${fontSize * 0.9}px`,
                      color: '#666',
                      fontWeight: 'medium'
                    }}
                  >
                    Notice Period: {candidateData.noticePeriod} days
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        // Single column layout would go here
        <Box>
          {/* Professional Summary */}
          {candidateData.headline && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: primaryColor,
                  fontWeight: 'bold',
                  mb: 2,
                  fontSize: `${fontSize * 1.2}px`,
                  borderBottom: `2px solid ${primaryColor}`,
                  paddingBottom: 1
                }}
              >
                PROFESSIONAL SUMMARY
              </Typography>
              <Typography sx={{ fontSize: `${fontSize}px`, lineHeight: 1.6 }}>
                {candidateData.headline}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ModernTemplate;